import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { register } from "../lib/api";

export default function Register() {
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next");

  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState<{ intent: "register" | "login" } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await register(email, username);
      setSent({ intent: res.intent });
    } catch (e) {
      const msg = (e as Error).message;
      setError(humanError(msg));
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <div className="max-w-prose space-y-4">
        <h1 className="text-3xl font-semibold">Check your email</h1>
        <p>
          We sent a magic link to <strong>{email}</strong>. Open it in this browser to{" "}
          {sent.intent === "register" ? "complete registration" : "sign in"}. The link
          expires in 15 minutes.
        </p>
        {next && (
          <p className="text-sm text-muted">
            After verification you'll be redirected to{" "}
            <code className="bg-sand px-1 rounded">{next}</code>.
          </p>
        )}
        <p className="text-sm text-muted">
          Didn't get it? Check your spam folder, then{" "}
          <button
            className="underline"
            onClick={() => {
              setSent(null);
              setError(null);
            }}
          >
            try again
          </button>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-prose space-y-6">
      <h1 className="text-3xl font-semibold">Register</h1>
      <p className="text-ink/80">
        Email + username. We don't ask for a password. We don't tie your username or
        email to your votes in any public response. You can change your cohort fields
        any time on your{" "}
        <Link to="/profile">profile page</Link>.
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input"
            autoComplete="email"
          />
        </div>
        <div>
          <label className="label" htmlFor="username">
            Username
          </label>
          <input
            id="username"
            type="text"
            required
            minLength={2}
            maxLength={32}
            pattern="[a-zA-Z0-9_.\-]{2,32}"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="input"
            autoComplete="username"
          />
          <p className="text-xs text-muted mt-1">
            2–32 chars. Letters, digits, dot, hyphen, underscore. If this email is
            already registered, the username field is ignored.
          </p>
        </div>
        {error && <p className="text-sm text-rust">{error}</p>}
        <button type="submit" disabled={submitting} className="btn-primary">
          {submitting ? "Sending…" : "Send magic link"}
        </button>
      </form>
    </div>
  );
}

function humanError(code: string): string {
  switch (code) {
    case "invalid_email":
      return "That email doesn't look right.";
    case "invalid_username":
      return "Username must be 2–32 chars: letters, digits, dot, hyphen, underscore.";
    case "username_taken":
      return "That username is taken. Try another.";
    case "rate_limited":
      return "Too many recent attempts. Try again in an hour.";
    case "email_send_failed":
      return "We couldn't send the email. Try again.";
    default:
      return `Error: ${code}`;
  }
}
